/**
 * Tenant / tenantUser tRPC client helpers.
 */

import { trpc } from "../trpc";
import { createDefaultTenantFeatures } from "../../shared/tenant";
import type {
  TenantContextValue,
  TenantFeatures,
  TenantRecord,
  TenantSummary,
  TenantUserListResult,
  TenantUserRecord,
} from "./types";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === "object" ? (value as UnknownRecord) : {};
}

function asString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  return fallback;
}

function mapTenantSummary(value: unknown, fallbackId = ""): TenantSummary {
  const row = asRecord(value);
  return {
    id: asString(row.id || row.tenantId || fallbackId),
    name: asString(row.name || row.tenantName || row.id || row.tenantId || "未命名租户"),
    code: (row.code as string | null | undefined) ?? null,
    status: asString(row.status || "active"),
  };
}

function mapFeatures(value: unknown): TenantFeatures {
  const row = asRecord(value);
  return createDefaultTenantFeatures({
    dispatch_monitor: asBoolean(row.dispatch_monitor, false),
    script_monitor: asBoolean(row.script_monitor, false),
    atmosphere_assistant: asBoolean(row.atmosphere_assistant, false),
    speech_points: asBoolean(row.speech_points, false),
    ai_management: asBoolean(row.ai_management, false),
  });
}

export function mapTenantContext(raw: unknown): TenantContextValue {
  const row = asRecord(raw);
  const homeTenant = mapTenantSummary(
    row.homeTenant,
    asString(row.homeSystemTenantId)
  );
  const activeTenant = mapTenantSummary(
    row.activeTenant,
    asString(row.activeSystemTenantId || homeTenant.id)
  );
  const role = asString(row.tenantRole || row.role || "member") as TenantContextValue["role"];
  const isPlatformSuperAdmin = asBoolean(
    row.isPlatformSuperAdmin ?? role === "platform_super_admin"
  );

  return {
    homeTenant,
    activeTenant,
    role,
    isPlatformSuperAdmin,
    features: mapFeatures(row.features),
    status: asString(row.status || activeTenant.status || "active"),
  };
}

export function mapTenantRecord(raw: unknown): TenantRecord {
  const row = asRecord(raw);
  const summary = mapTenantSummary(row);
  return {
    ...summary,
    features: mapFeatures(row.features),
    createdAt: (row.createdAt as string | Date | null | undefined) ?? null,
    updatedAt: (row.updatedAt as string | Date | null | undefined) ?? null,
    remark: (row.remark as string | null | undefined) ?? null,
  };
}

export function mapTenantUser(raw: unknown): TenantUserRecord {
  const row = asRecord(raw);
  return {
    id: asString(row.id || row.userId),
    username: (row.username as string | null | undefined) ?? null,
    name: asString(row.name || row.username || row.email || row.id),
    email: (row.email as string | null | undefined) ?? null,
    role: asString(row.tenantRole || row.role || "member"),
    banned: asBoolean(row.banned),
    banReason: (row.banReason as string | null | undefined) ?? null,
    createdAt: (row.createdAt as string | Date | null | undefined) ?? null,
    updatedAt: (row.updatedAt as string | Date | null | undefined) ?? null,
  };
}

export function getTrpcErrorMessage(error: unknown, fallback: string) {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  const row = asRecord(error);
  const message =
    asString(row.message) ||
    asString(asRecord(row.data).message) ||
    asString(asRecord(row.shape).message);
  return message || fallback;
}

export const tenantApi = {
  async context(): Promise<TenantContextValue> {
    const raw = await trpc.tenant.context.query();
    return mapTenantContext(raw);
  },

  async list(): Promise<TenantRecord[]> {
    const raw = await trpc.tenant.list.query();
    return (Array.isArray(raw) ? raw : []).map(mapTenantRecord);
  },

  async create(input: {
    name: string;
    code: string;
    features?: Partial<TenantFeatures>;
  }): Promise<TenantRecord> {
    const raw = await trpc.tenant.create.mutate({
      name: input.name,
      code: input.code,
      features: input.features,
    });
    return mapTenantRecord(raw);
  },

  async update(input: {
    tenantId: string;
    name: string;
  }): Promise<TenantRecord> {
    const raw = await trpc.tenant.update.mutate(input);
    return mapTenantRecord(raw);
  },

  async updateStatus(input: {
    tenantId: string;
    status: "active" | "disabled";
  }): Promise<TenantRecord> {
    const raw = await trpc.tenant.status.mutate(input);
    return mapTenantRecord(raw);
  },

  async updateFeatures(input: {
    tenantId: string;
    features: Partial<TenantFeatures>;
  }): Promise<TenantRecord> {
    const raw = await trpc.tenant.features.mutate(input);
    return mapTenantRecord(raw);
  },

  async switch(input: { tenantId: string }) {
    return trpc.tenant.switch.mutate(input);
  },
};

export const tenantUserApi = {
  async list(): Promise<TenantUserListResult> {
    const raw = await trpc.tenantUser.list.query();
    const users = (Array.isArray(raw) ? raw : []).map(mapTenantUser);
    return { users, total: users.length };
  },

  async create(input: {
    username: string;
    name: string;
    password: string;
    role: "tenant_admin" | "member";
  }): Promise<TenantUserRecord> {
    const raw = await trpc.tenantUser.create.mutate({
      username: input.username,
      name: input.name,
      password: input.password,
      tenantRole: input.role,
    });
    return mapTenantUser(raw);
  },

  async updateRole(input: {
    userId: string;
    role: "tenant_admin" | "member";
  }): Promise<TenantUserRecord> {
    const raw = await trpc.tenantUser.updateRole.mutate({
      userId: input.userId,
      tenantRole: input.role,
    });
    return mapTenantUser(raw);
  },

  async setPassword(input: {
    userId: string;
    password: string;
  }): Promise<unknown> {
    return trpc.tenantUser.setPassword.mutate({
      userId: input.userId,
      newPassword: input.password,
    });
  },

  async ban(input: {
    userId: string;
    reason?: string;
  }): Promise<unknown> {
    return trpc.tenantUser.ban.mutate(input);
  },

  async unban(input: { userId: string }): Promise<unknown> {
    return trpc.tenantUser.unban.mutate(input);
  },
};
